import { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { api, fileUrl } from "@/lib/api";
import { ArrowRight, Loader2 } from "lucide-react";
import SearchInput from "@/components/SearchInput";

const titleCase = (s) => (s || "").replace(/[-_]/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

const ServicesPage = () => {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("");

  useEffect(() => {
    api.get("/print-services?active_only=true").then(({ data }) => setServices(data || []))
      .finally(() => setLoading(false));
  }, []);

  const grouped = useMemo(() => {
    const map = {};
    services
      .filter((s) => !filter || s.name.toLowerCase().includes(filter.toLowerCase()) || (s.category || "").toLowerCase().includes(filter.toLowerCase()))
      .forEach((s) => {
        const cat = s.category || "Other";
        (map[cat] ||= []).push(s);
      });
    // Sort categories alphabetically, services by sort_order then name
    const out = Object.keys(map).sort().map((cat) => ({
      category: cat,
      items: map[cat].sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0) || a.name.localeCompare(b.name)),
    }));
    return out;
  }, [services, filter]);

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 pav-fade-in" data-testid="services-page">
      <div className="mb-10">
        <p className="text-xs uppercase tracking-[0.2em] text-[#06B6D4] font-semibold">Catalog</p>
        <h1 className="font-display text-4xl sm:text-5xl font-extrabold text-[#0B1E3F] mt-2">All services</h1>
        <p className="mt-3 text-neutral-600 max-w-2xl">Explore every print service we offer. For custom tarpaulins, jump straight to the live calculator.</p>
        <div className="mt-6 max-w-md">
          <SearchInput
            value={filter}
            onChange={setFilter}
            placeholder="Search services or categories…"
            data-testid="services-search" />
        </div>
      </div>

      {loading ? (
        <div className="text-center py-20 text-neutral-500">
          <Loader2 className="h-8 w-8 mx-auto animate-spin text-[#06B6D4]" />
          <p className="mt-3">Loading services…</p>
        </div>
      ) : grouped.length === 0 ? (
        <div className="text-center py-20 text-neutral-500" data-testid="services-empty">
          No services to show right now.
        </div>
      ) : grouped.map((g) => (
        <section key={g.category} className="mb-12" data-testid={`category-${g.category.replace(/\s+/g, "-").toLowerCase()}`}>
          <div className="flex items-end justify-between mb-5">
            <h2 className="font-display text-2xl font-bold text-[#1A1A1A]">{titleCase(g.category)}</h2>
            <Link to="/order" className="text-sm font-semibold text-[#0B1E3F] hover:underline inline-flex items-center gap-1">
              Order now <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6">
            {g.items.map((s) => (
              <Link to={`/order?service=${s.service_id}`} key={s.service_id}
                data-testid={`service-card-${s.service_id}`}
                className="pav-card group p-0 overflow-hidden flex flex-col h-full">
                <div className="aspect-[4/3] overflow-hidden bg-[#F4F6FB]">
                  {s.cover_photo || s.photos?.[0] ? (
                    <img src={fileUrl(s.cover_photo || s.photos[0])} alt={s.name}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-neutral-300 text-xs">No image</div>
                  )}
                </div>
                <div className="p-4">
                  <h3 className="font-semibold text-[#1A1A1A]">{s.name}</h3>
                  <p className="text-sm text-neutral-500 mt-1">
                    {s.base_price ? `From ₱${Number(s.base_price).toLocaleString()} ${s.unit || "/ piece"}` : (s.short_description || "")}
                  </p>
                </div>
              </Link>
            ))}
          </div>
        </section>
      ))}
    </div>
  );
};

export default ServicesPage;
